import { useEffect, useState } from 'react';
import { ApiError, request } from '../lib/api-client.ts';
import { clearToken, getToken } from '../lib/auth-storage.ts';
import { canAutoStart, finishPortalArrival, startFromPortal } from '../lib/portal-arrival.ts';
import type { AuthUser } from '../types/auth.ts';

/**
 * セッション状態（current user の取得 + ローディング管理）。
 *
 * ポータルから戻った直後（引換番号）は、先にこのアプリのログインに替えてから
 * current user を取る（lib/portal-arrival.ts の finishPortalArrival）。その間は loading のまま。
 *
 * StrictMode / HMR の注意:
 *   effect が2回走っても、前の回の結果で状態を変えないよう、回ごとの active で止める。
 *   番号の引き換えは finishPortalArrival が1回だけにする。
 *
 * 期限切れトークンの処理:
 *   /auth/me が 401 を返した場合、期限切れ確定なので localStorage を必ず削除する
 *   （api-client の 401 ハンドラは /auth/me を除外しているため、ここで明示クリア）。
 *   ポータルのアプリ一覧から来ていたら（portal_login=1）、そのままポータルのログインを始める。
 */

type State =
  | { status: 'loading' }
  | { status: 'authenticated'; user: AuthUser }
  | { status: 'guest' };

type MeResponse = { user: AuthUser };

export const useAuth = (): State & { logout: () => void } => {
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    void finishPortalArrival().then((arrival) => {
      // ポータルへ移動中は loading のまま（ログイン画面を一瞬出さない）
      if (!active || arrival === 'redirecting') return;

      const token = getToken();
      if (!token) {
        setState({ status: 'guest' });
        return;
      }

      request<MeResponse>('/auth/me', { signal: controller.signal })
        .then((data) => {
          if (active) setState({ status: 'authenticated', user: data.user });
        })
        .catch((err) => {
          if (!active) return;
          if (err instanceof ApiError && err.status === 401) {
            clearToken();
            if (canAutoStart) {
              void startFromPortal().then((started) => {
                if (active && started !== 'redirecting') setState({ status: 'guest' });
              });
              return;
            }
          }
          setState({ status: 'guest' });
        });
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  const logout = (): void => {
    clearToken();
    window.location.replace('/login');
  };

  return { ...state, logout };
};
